import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { useTranslation } from '@/hooks/useTranslation';
import { Link } from 'react-router-dom';
import type { NavigationItem as NavigationItemType } from './types';

interface NavigationItemProps {
  item: NavigationItemType;
  isActive: (path: string) => boolean;
  isSubItem?: boolean;
}

export const NavigationItem = ({
  item,
  isActive,
  isSubItem = false,
}: NavigationItemProps) => {
  const { t, tDynamic } = useTranslation();
  const itemPath = item.path || item.url || '#';
  const isItemActive = item.path ? isActive(item.path) : false;
  const isExternal = item.external === true;
  const translatedName = tDynamic(item.name);
  const ariaLabel = t('navigation.navigateTo', { name: translatedName });

  // Shared label content for every link variant.
  const linkContent = (
    <>
      <item.icon className='h-4 w-4' />
      <span>{translatedName}</span>
    </>
  );

  // The clickable link differs only by internal (React Router) vs external.
  const link = isExternal ? (
    <a
      href={itemPath}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={ariaLabel}
    >
      {linkContent}
    </a>
  ) : (
    <Link
      to={itemPath}
      aria-label={ariaLabel}
      aria-current={isItemActive ? 'page' : undefined}
    >
      {linkContent}
    </Link>
  );

  if (isSubItem) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild isActive={isItemActive}>
          {link}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={translatedName}>
        {link}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
